import type { Lang } from './types';
import { ownerStrings, staffStrings } from './strings';

/**
 * Help articles (company standard, section 6): one for each Tier-1 entry in docs/SUPPORT.md, which
 * names it as `help:<id>`. Shown in the owner app (Más → Ayuda) and on dejodicho.com/ayuda. Each can
 * be found by the exact message the owner saw on screen (`messages`), in either language.
 * A test checks every `help:<id>` the playbook names exists here.
 */
export interface HelpArticle {
  id: string;
  title: Record<Lang, string>;
  body: Record<Lang, string[]>;
  /** On-screen messages this article explains, so searching for what the screen said finds it. */
  messages: Record<Lang, string>[];
}

const o = (key: keyof typeof ownerStrings) => ownerStrings[key] as Record<Lang, string>;
const staff = (key: keyof typeof staffStrings.es) => ({ es: staffStrings.es[key], en: (staffStrings.en as Record<string, string>)[key] ?? '' });

export const SUPPORT_ADDRESS = 'support@dejodicho.com';

export const helpArticles: HelpArticle[] = [
  {
    id: 'sign-in-code',
    title: { es: 'No me llega el código, o dice que es incorrecto', en: "The sign-in code doesn't arrive, or says it's wrong" },
    body: {
      es: [
        'El código llega por correo desde codigo@mail.dejodicho.com, con el asunto "Tu código de Dejo Dicho". Revisa también Spam.',
        'Cada código dura 10 minutos y solo vale el más reciente. Si pediste varios, usa el último.',
        'Puedes pedir hasta 5 códigos por hora con el mismo correo.',
        'Si escribiste mal el correo, toca "Usar otro correo" y pide un código nuevo.',
      ],
      en: [
        'The code comes by email from codigo@mail.dejodicho.com, with the subject "Tu código de Dejo Dicho". Check Spam too.',
        'Each code lasts 10 minutes and only the newest one works. If you asked for several, use the last one.',
        'You can ask for up to 5 codes an hour with the same email.',
        'If you mistyped the email, tap "Use a different email" and ask for a new code.',
      ],
    },
    messages: [o('errWrongCode'), o('errCodeExpired'), o('errTooMany')],
  },
  {
    id: 'lost-phone',
    title: { es: 'Perdí mi teléfono, o alguien más puede estar en mi cuenta', en: 'I lost my phone, or someone else may be in my account' },
    body: {
      es: [
        'Entra desde otro teléfono o computadora en app.dejodicho.com.',
        'Ve a Más → Dispositivos con sesión iniciada. Verás cada teléfono y computadora, y cuándo se usó por última vez. Toca Cerrar sesión en el que perdiste.',
        'O toca Cerrar sesión en todos los dispositivos para cerrarlas todas a la vez.',
        'Las sesiones se cierran solas después de 30 días sin uso, y siempre después de 180 días.',
        `Si también perdiste el acceso a tu correo, escríbenos a ${SUPPORT_ADDRESS}.`,
      ],
      en: [
        'Sign in from another phone or computer at app.dejodicho.com.',
        "Go to Más → Signed-in devices. You'll see each phone and computer and when it was last used. Tap Sign out on the one you lost.",
        'Or tap Sign out on all devices to end them all at once.',
        'Sign-ins end by themselves after 30 days unused, and always after 180 days.',
        `If you also lost access to your email, write to us at ${SUPPORT_ADDRESS}.`,
      ],
    },
    messages: [],
  },
  {
    id: 'second-person',
    title: { es: 'Invitar a otra persona de la casa', en: 'Inviting another person in the home' },
    body: {
      es: [
        'En Más → Otra persona de la casa, escribe su correo y toca Invitar. Una casa puede tener dos dueños.',
        'No hace falta el correo de invitación: la otra persona entra en app.dejodicho.com con ese correo y se une a tu casa. La invitación dura 14 días.',
        'Si el correo estaba mal, cancela la invitación e invita de nuevo.',
      ],
      en: [
        'In Más → Another person in the home, type their email and tap Invite. A home can have two owners.',
        "The invitation email isn't needed: the other person signs in at app.dejodicho.com with that email and joins your home. The invitation lasts 14 days.",
        'If the email was wrong, cancel the invitation and invite again.',
      ],
    },
    messages: [o('ownerLimit')],
  },
  {
    id: 'too-many-requests',
    title: { es: 'Dice que hay demasiados intentos', en: 'It says too many attempts' },
    body: {
      es: [
        'Es una protección contra intentos repetidos desde la misma conexión.',
        'Espera una hora, o cambia el teléfono de Wi-Fi a datos móviles, y vuelve a intentar.',
      ],
      en: [
        "It's a protection against repeated attempts from the same connection.",
        'Wait an hour, or switch the phone from Wi-Fi to mobile data, and try again.',
      ],
    },
    messages: [o('errTooManyRequests'), o('errTooMany')],
  },
  {
    id: 'worker-link',
    title: { es: 'El enlace de la persona no abre', en: "The worker's link doesn't open" },
    body: {
      es: [
        'En Más, la tarjeta de cada persona dice si su enlace está activo.',
        '"Este enlace ya no funciona" quiere decir que se creó un enlace nuevo, que se desactivó, o que se quitó a la persona. Toca Activar enlace o Crear enlace nuevo, y luego Enviar enlace.',
        'El enlace se abre en el navegador del teléfono; no hay que instalar nada.',
        'Para hoy: imprime la lista del día desde Rutinas → Ver lista en PDF, o léesela.',
      ],
      en: [
        "In Más, each worker's card says whether their link is on.",
        '"This link no longer works" means a new link was made, the link was turned off, or the worker was removed. Tap Turn on link or Make a new link, then Send link.',
        "The link opens in the phone's browser; there is nothing to install.",
        "For today: print the day's list from Rutinas → View list as PDF, or read it to them.",
      ],
    },
    messages: [o('linkOff'), staff('linkGone')],
  },
  {
    id: 'checks-not-showing',
    title: { es: 'Marcó una tarea y no me aparece', en: "A task was checked but I don't see it" },
    body: {
      es: [
        'Las marcas se guardan primero en el teléfono de la persona y se envían cuando hay señal. La app se actualiza cada 10 segundos.',
        'La hora que ves es la hora en que se marcó, aunque llegue después.',
        'Si es una tarea del jardín, cambia a Jardín arriba en Inicio.',
        'Pídele que abra la página otra vez con señal.',
      ],
      en: [
        "Checks are saved on the worker's phone first and sent when there's signal. The app refreshes every 10 seconds.",
        'The time you see is when it was checked, even if it arrives later.',
        "If it's a garden task, switch to Garden at the top of Home.",
        'Ask them to open the page again with signal.',
      ],
    },
    messages: [staff('offline')],
  },
  {
    id: 'translation',
    title: { es: 'La traducción no aparece o no está bien', en: "The translation is missing or wrong" },
    body: {
      es: [
        'Lo que escribes se traduce solo al idioma que lee cada persona, español o inglés.',
        'Cada casa tiene 100 traducciones al mes, y 300 más en sus primeros 30 días. Lo ves en Más → Traducciones este mes.',
        'Al llegar al límite, el texto nuevo se muestra en el idioma original hasta el mes siguiente.',
        'Puedes corregir o escribir la traducción tú mismo en el editor de la tarea, en "Español para el personal". La máquina nunca la cambia después.',
      ],
      en: [
        'What you write is translated automatically into the language each worker reads, Spanish or English.',
        'Each home has 100 translations a month, plus 300 more in its first 30 days. You can see it in Más → Translations this month.',
        'At the limit, new text shows in the original language until next month.',
        'You can correct or write the translation yourself in the task editor, under "Spanish for staff". The machine never changes it afterwards.',
      ],
    },
    messages: [o('translationsSub')],
  },
  {
    id: 'daily-report',
    title: { es: 'No me llegó el reporte del día', en: "The daily report didn't arrive" },
    body: {
      es: [
        'El día de cada equipo se cierra cuando se marcan todas las tareas, o a su hora de salida (Más → Días y horario de trabajo; el jardín tiene el suyo).',
        'El reporte siempre está en Reportes, también el del día en curso.',
        'Para recibirlo por correo, activa "Enviarme el reporte diario por correo" en Más. Si no lo ves, revisa Spam.',
      ],
      en: [
        "Each crew's day closes when every task is checked, or at its end time (Más → Work days and hours; the garden has its own).",
        "The report is always in Reports, including the day in progress.",
        "To get it by email, turn on \"Email me the daily report\" in Más. If you don't see it, check Spam.",
      ],
    },
    messages: [],
  },
  {
    id: 'delete-account',
    title: { es: 'Borrar mi cuenta o descargar mis datos', en: 'Deleting my account or downloading my data' },
    body: {
      es: [
        'Más → Descargar mis datos te da un archivo con todo lo que guardamos de ti y de tu casa.',
        'Más → Eliminar mi cuenta borra tu cuenta. Si eres el único dueño, se borra toda la casa: cuartos, tareas, reportes y enlaces.',
        'También puedes borrarla sin la app, en dejodicho.com/borrar-cuenta.',
        'Las copias de seguridad se borran solas: las diarias en 14 días y las mensuales en hasta 3 meses.',
        'Borrar la cuenta no cancela una suscripción de la tienda.',
      ],
      en: [
        'Más → Download my data gives you a file with everything we keep about you and your home.',
        "Más → Delete my account deletes your account. If you're the only owner, the whole home is deleted: rooms, tasks, reports, and links.",
        'You can also delete it without the app at dejodicho.com/en/delete-account.',
        'Backups delete themselves: daily ones within 14 days and monthly ones within 3 months.',
        "Deleting the account doesn't cancel a store subscription.",
      ],
    },
    messages: [],
  },
  {
    id: 'cancel',
    title: { es: 'Cancelar o pedir un reembolso', en: 'Cancelling or asking for a refund' },
    body: {
      es: [
        'Todavía no hay suscripciones de pago: no se cobra nada.',
        'Cuando las haya, se comprarán y se cancelarán en la App Store o en Google Play, y aquí explicaremos cómo.',
      ],
      en: [
        "There are no paid subscriptions yet: nothing is charged.",
        "When there are, they'll be bought and cancelled in the App Store or Google Play, and we'll explain how here.",
      ],
    },
    messages: [],
  },
];

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\{[^}]+\}/g, ' ');

/**
 * Articles matching what the owner typed, best first. A message copied from the screen (in either
 * language) finds its article; otherwise every word typed must appear in the article.
 */
export function searchHelp(query: string, lang: Lang): HelpArticle[] {
  const q = fold(query).trim();
  if (!q) return helpArticles;
  const words = q.split(/\s+/).filter((w) => w.length > 1);
  const scored = helpArticles.map((a) => {
    const messages = a.messages.flatMap((m) => [m.es, m.en]).map(fold);
    const exact = messages.some((m) => m.trim() && (m.includes(q) || q.includes(m.trim())));
    const text = fold([a.title.es, a.title.en, ...a.body[lang], ...messages].join(' '));
    const hits = words.filter((w) => text.includes(w)).length;
    return { a, score: exact ? 1000 : hits === words.length ? hits : 0 };
  });
  return scored.filter((s) => s.score > 0).sort((x, y) => y.score - x.score).map((s) => s.a);
}
